import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";
import { toast } from "sonner";
import {
  OverviewTab,
  ContractorsTab,
  TransactionsTab,
  JobsTab,
  DisputesTab,
  QuizStatsTab,
} from "@/components/admin/AdminTabs";
import AdminABTests from "@/components/admin/AdminABTests";
import AdminInsights from "@/components/admin/AdminInsights";
import { logger } from "@/lib/logger";

const TABS = ["overview", "insights", "contractors", "categories", "jobs", "transactions", "disputes", "quiz", "ab"];
const TAB_LABEL = { ab: "A/B" };

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({
    stats: null, contractors: [], txns: [], cats: [], jobs: [], disputes: [], quizStats: null, defaultsBySlug: {},
  });
  const [editing, setEditing] = useState({});
  const [newCat, setNewCat] = useState({ name: "", slug: "", icon: "wrench", lead_price: 15 });

  const refresh = useCallback(async () => {
    try {
      const [s, c, t, k, j, d, q, defs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/contractors"),
        api.get("/admin/transactions"),
        api.get("/categories"),
        api.get("/admin/jobs"),
        api.get("/admin/disputes"),
        api.get("/admin/quiz/stats"),
        api.get("/categories/defaults"),
      ]);
      const defaultsBySlug = Object.fromEntries((defs.data || []).map((d) => [d.slug, d.default_price]));
      setData({
        stats: s.data, contractors: c.data, txns: t.data, cats: k.data,
        jobs: j.data, disputes: d.data, quizStats: q.data, defaultsBySlug,
      });
    } catch (err) {
      logger.error("[admin] refresh failed", err);
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const approve = async (id) => { await api.post(`/admin/contractors/${id}/approve`); toast.success("Approved"); refresh(); };
  const reject = async (id) => { await api.post(`/admin/contractors/${id}/reject`); toast.success("Rejected"); refresh(); };
  const refundDispute = async (id) => { const r = await api.post(`/admin/disputes/${id}/refund`); toast.success(`Refunded $${r.data.refund.toFixed(2)}`); refresh(); };
  const rejectDispute = async (id) => { await api.post(`/admin/disputes/${id}/reject`); toast.success("Dispute rejected"); refresh(); };

  const saveCat = async (c) => {
    const formData = editing[c.category_id] || c;
    await api.put(`/categories/${c.category_id}`, {
      name: formData.name, slug: formData.slug, icon: formData.icon, lead_price: Number(formData.lead_price),
    });
    toast.success("Updated");
    setEditing((e) => { const n = { ...e }; delete n[c.category_id]; return n; });
    refresh();
  };
  const deleteCat = async (id) => { await api.delete(`/categories/${id}`); toast.success("Deleted"); refresh(); };
  const restoreDefault = async (c) => {
    const def = data.defaultsBySlug?.[c.slug];
    if (def == null) return toast.error("No default price for this category");
    if (Number(c.lead_price) === Number(def)) return toast.info(`Already at default ($${def})`);
    await api.put(`/categories/${c.category_id}`, {
      name: c.name, slug: c.slug, icon: c.icon, lead_price: Number(def),
    });
    toast.success(`Restored to $${def}`);
    setEditing((e) => { const n = { ...e }; delete n[c.category_id]; return n; });
    refresh();
  };
  const addCat = async () => {
    if (!newCat.name || !newCat.slug) return toast.error("Name and slug required");
    await api.post("/categories", { ...newCat, lead_price: Number(newCat.lead_price) });
    toast.success("Created");
    setNewCat({ name: "", slug: "", icon: "wrench", lead_price: 15 });
    refresh();
  };

  const { stats, contractors, txns, cats, jobs, disputes, quizStats, defaultsBySlug } = data;

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="overline mb-3">// Admin control room</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Owner dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-2 border-ink mt-10 mb-10">
          <Stat label="Revenue" n={`$${stats?.revenue_total?.toFixed(2) ?? 0}`} hot />
          <Stat label="Users" n={stats?.total_users ?? 0} />
          <Stat label="Open jobs" n={stats?.open_jobs ?? 0} />
          <Stat label="Lead unlocks" n={stats?.lead_unlocks ?? 0} last />
        </div>

        <div className="flex flex-wrap gap-0 border-2 border-ink mb-8">
          {TABS.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 min-w-[120px] py-3 font-mono uppercase text-xs tracking-widest font-bold ${i > 0 ? "border-l-2 border-ink" : ""} ${tab === t ? "bg-ink text-acid" : "bg-white"}`}
              data-testid={`admin-tab-${t}`}
            >
              {TAB_LABEL[t] || t}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab stats={stats} />}
        {tab === "insights" && <AdminInsights />}
        {tab === "contractors" && <ContractorsTab contractors={contractors} onApprove={approve} onReject={reject} />}
        {tab === "jobs" && <JobsTab jobs={jobs} />}
        {tab === "transactions" && <TransactionsTab txns={txns} />}
        {tab === "disputes" && <DisputesTab disputes={disputes} onRefund={refundDispute} onReject={rejectDispute} />}
        {tab === "quiz" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-ink mb-6">
              <Stat label="Quiz completions" n={quizStats?.totals?.completions ?? 0} />
              <Stat label="→ Jobs posted" n={quizStats?.totals?.conversions ?? 0} />
              <Stat label="Conversion rate" n={`${quizStats?.totals?.rate ?? 0}%`} hot last />
            </div>
            <QuizStatsTab quizStats={quizStats} />
          </>
        )}
        {tab === "categories" && (
          <CategoriesEditor
            cats={cats} editing={editing} setEditing={setEditing}
            newCat={newCat} setNewCat={setNewCat}
            defaultsBySlug={defaultsBySlug}
            onSave={saveCat} onDelete={deleteCat} onAdd={addCat} onRestore={restoreDefault}
          />
        )}
        {tab === "ab" && <AdminABTests />}
      </div>
    </div>
  );
}

function CategoriesEditor({ cats, editing, setEditing, newCat, setNewCat, defaultsBySlug = {}, onSave, onDelete, onAdd, onRestore }) {
  return (
    <>
      <div className="brut-card bg-white p-6 mb-6">
        <div className="overline mb-3">Add new category</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input className="brut-input" placeholder="Name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="newcat-name" />
          <input className="brut-input" placeholder="slug" value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} data-testid="newcat-slug" />
          <input className="brut-input" placeholder="icon (lucide name)" value={newCat.icon} onChange={(e) => setNewCat({ ...newCat, icon: e.target.value })} data-testid="newcat-icon" />
          <input type="number" className="brut-input" placeholder="Lead price" value={newCat.lead_price} onChange={(e) => setNewCat({ ...newCat, lead_price: e.target.value })} data-testid="newcat-price" />
        </div>
        <button onClick={onAdd} className="brut-btn-primary mt-4" data-testid="newcat-add">Add category</button>
      </div>
      <div className="brut-card bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink text-acid"><tr>
            <th className="text-left p-3 font-mono uppercase">Name</th>
            <th className="text-left p-3 font-mono uppercase">Slug</th>
            <th className="text-left p-3 font-mono uppercase">Lead Price</th>
            <th className="text-left p-3 font-mono uppercase">Actions</th>
          </tr></thead>
          <tbody>
            {cats.map((c) => {
              const e = editing[c.category_id];
              const suggested = defaultsBySlug[c.slug];
              const currentPrice = Number(e?.lead_price ?? c.lead_price);
              const hasSuggestion = suggested != null;
              const isDefault = hasSuggestion && currentPrice === Number(suggested);
              return (
                <tr key={c.category_id} className="border-t-2 border-ink">
                  <td className="p-3">
                    <input
                      className="brut-input py-1.5"
                      value={e?.name ?? c.name}
                      onChange={(ev) => setEditing((x) => ({ ...x, [c.category_id]: { ...c, ...x[c.category_id], name: ev.target.value } }))}
                    />
                  </td>
                  <td className="p-3 font-mono text-xs">{c.slug}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="number"
                        className="brut-input py-1.5 max-w-[100px]"
                        value={e?.lead_price ?? c.lead_price}
                        onChange={(ev) => setEditing((x) => ({ ...x, [c.category_id]: { ...c, ...x[c.category_id], lead_price: ev.target.value } }))}
                        data-testid={`price-${c.slug}`}
                      />
                      {hasSuggestion && (
                        <span
                          className={`font-mono text-[10px] uppercase tracking-wider px-2 py-1 border-2 border-ink ${isDefault ? "bg-acid text-ink" : "bg-white text-ink"}`}
                          title={isDefault ? "Matches suggested market rate" : `Suggested market rate: $${suggested}`}
                          data-testid={`suggested-${c.slug}`}
                        >
                          {isDefault ? `✓ SUG $${suggested}` : `SUG $${suggested}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 space-x-2 whitespace-nowrap">
                    <button onClick={() => onSave(c)} className="brut-btn-primary text-[10px] px-3 py-2" data-testid={`save-${c.slug}`}>Save</button>
                    {hasSuggestion && (
                      <button
                        onClick={() => onRestore(c)}
                        disabled={isDefault}
                        className="brut-btn text-[10px] px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        title={isDefault ? "Already at default" : `Restore to $${suggested}`}
                        data-testid={`restore-${c.slug}`}
                      >
                        Restore default
                      </button>
                    )}
                    <button onClick={() => onDelete(c.category_id)} className="brut-btn text-[10px] px-3 py-2">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Stat({ label, n, last, hot }) {
  return (
    <div className={`p-6 ${hot ? "bg-acid" : "bg-white"} ${!last ? "border-r-2 border-ink" : ""}`}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-3xl mt-2 truncate">{n}</div>
    </div>
  );
}
